import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import SearchBar from './components/SearchBar';
import SearchFilters from './components/SearchFilters';
import JobCard from './components/JobCard';
import WalkinDriveCard from './components/WalkinDriveCard';
import QuickStats from './components/QuickStats';
import JobAlerts from './components/JobAlerts';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import axios from 'axios';
import JobDetailsModal from './components/JobDetailsModal';
import ApplyModal from './components/ApplyModal';
import CandidateAuthModal from './components/CandidateAuthModal';
import candidateAuthService from '../../services/candidateAuthService';

const PendingAiScreeningCard = ({ sc, navigate }) => {
  const calculateRemaining = React.useCallback(() => {
    if (sc.is_expired || sc.ai_screening_status === 'EXPIRED') {
      return 0;
    }
    let targetTime = null;
    if (sc.ai_screening_deadline) {
      targetTime = new Date(sc.ai_screening_deadline).getTime();
    } else if (typeof sc.remaining_seconds === 'number' && sc.remaining_seconds > 0) {
      if (!sc._deadlineTs) {
        sc._deadlineTs = Date.now() + sc.remaining_seconds * 1000;
      }
      targetTime = sc._deadlineTs;
    } else if (sc.created_at || sc.applied_at) {
      const created = new Date(sc.created_at || sc.applied_at).getTime();
      targetTime = created + 24 * 3600 * 1000;
    }

    if (!targetTime) return 0;
    const now = Date.now();
    return Math.max(0, Math.floor((targetTime - now) / 1000));
  }, [sc]);

  const [remainingSecs, setRemainingSecs] = React.useState(calculateRemaining());

  React.useEffect(() => {
    setRemainingSecs(calculateRemaining());
    const interval = setInterval(() => {
      const rem = calculateRemaining();
      setRemainingSecs(rem);
      if (rem <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [calculateRemaining]);

  const isExpired = sc.is_expired || sc.ai_screening_status === 'EXPIRED' || remainingSecs <= 0;

  const handleClick = () => {
    if (!isExpired) {
      navigate(`/ai-screening/${sc.id}`);
    }
  };

  if (isExpired) {
    return (
      <div className="bg-gradient-to-r from-red-500/10 via-red-500/5 to-red-500/10 border border-red-500/30 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-md">
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-red-500/20 text-red-400 border border-red-500/30 flex items-center justify-center flex-shrink-0 font-bold">
            <Icon name="AlertTriangle" size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-xs font-bold text-red-400 uppercase tracking-wider bg-red-500/20 px-2.5 py-0.5 rounded-full border border-red-500/30">
                AI Screening Deadline Expired
              </span>
            </div>
            <h3 className="text-base font-bold text-foreground">
              {sc.job_title || sc.position} at {sc.job_company || 'Employer'}
            </h3>
            <p className="text-xs text-muted-foreground">
              The 24-hour deadline for this AI voice screening has passed.
            </p>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          disabled
          className="w-full sm:w-auto border-red-500/30 text-red-400 opacity-60 font-semibold cursor-not-allowed"
        >
          <span>Deadline Expired</span>
        </Button>
      </div>
    );
  }

  const hours = Math.floor(remainingSecs / 3600);
  const minutes = Math.floor((remainingSecs % 3600) / 60);
  const formattedTime = hours > 0 ? `${hours}h ${minutes}m remaining` : `${minutes}m ${remainingSecs % 60}s remaining`;

  return (
    <div
      className="bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/15 border border-amber-500/30 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-lg shadow-amber-500/5 cursor-pointer hover:border-amber-500/50 transition-all"
      onClick={handleClick}
    >
      <div className="flex items-start gap-3.5">
        <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-500 border border-amber-500/30 flex items-center justify-center flex-shrink-0 font-bold">
          <Icon name="Mic" size={20} />
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs font-bold text-amber-500 uppercase tracking-wider bg-amber-500/20 px-2.5 py-0.5 rounded-full border border-amber-500/30">
              AI Voice Screening Pending — {formattedTime}
            </span>
          </div>
          <h3 className="text-base font-bold text-foreground">
            {sc.job_title || sc.position} at {sc.job_company || 'Employer'}
          </h3>
          <p className="text-xs text-muted-foreground">
            Complete your AI voice screening interview to proceed to the next hiring stage.
          </p>
        </div>
      </div>

      <Button
        variant="default"
        size="sm"
        className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold whitespace-nowrap shadow-md"
        onClick={(e) => {
          e.stopPropagation();
          handleClick();
        }}
      >
        <Icon name="Play" size={14} className="mr-1.5" />
        <span>CONTINUE AI INTERVIEW</span>
      </Button>
    </div>
  );
};

const JobMarketplace = () => {
  const [savedJobs, setSavedJobs] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [sortBy, setSortBy] = React.useState('relevance');
  const [viewMode, setViewMode] = React.useState('list');
  const [jobs, setJobs] = React.useState([]);
  const [walkinDrives, setWalkinDrives] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedJob, setSelectedJob] = React.useState(null);
  const [showApplyModal, setShowApplyModal] = React.useState(false);
  const [applySuccess, setApplySuccess] = React.useState(false);
  const [showCandidateAuthModal, setShowCandidateAuthModal] = React.useState(false);
  const [pendingJob, setPendingJob] = React.useState(null);

  const [searchParams, setSearchParams] = React.useState({
    keyword: '',
    location: ''
  });

  const [filters, setFilters] = React.useState({
    keyword: '',
    location: '',
    jobType: [],
    experience: '',
    salary: { min: '', max: '' },
    companySize: [],
    industry: '',
    postedWithin: ''
  });

  const navigate = useNavigate();
  const jobsPerPage = 10;

  const getApiUrl = () => {
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      return '/api';
    }
    return import.meta.env.VITE_API_URL || '/api';
  };
  const API_URL = getApiUrl();

  const stats = {
    activeJobs: 12847,
    companies: 3456,
    jobSeekers: 89234,
    placements: 45678
  };

  const fetchJobs = async (customParams = {}) => {
    try {
      setLoading(true);
      const activeCand = candidateAuthService.getCurrentCandidate() || JSON.parse(localStorage.getItem('user') || '{}');
      const params = { ...customParams };
      if (activeCand?.email) {
        params.email = activeCand.email;
      }
      if (activeCand?.id) {
        params.candidate_id = activeCand.id;
      }
      const headers = candidateAuthService.getAuthHeaders();
      const response = await axios.get(`${API_URL}/marketplace-jobs`, { params, headers });

      if (response.data.success) {
        const mappedJobs = response.data.data.map(job => ({
          ...job,
          id: job.source === 'client_requirement' ? job.id : parseInt(job.id),
          companyLogo: job.company_logo,
          salaryMin: parseInt(job.salary_min) || 0,
          salaryMax: parseInt(job.salary_max) || 0,
          postedDate: job.posted_at,
          applicants: parseInt(job.applicants) || 0,
          has_applied: Boolean(job.has_applied)
        }));
        setJobs(mappedJobs);
      }
    } catch (err) {
      setError("Failed to fetch jobs. Please try again later.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWalkinDrives = async (customParams = {}) => {
    try {
      const response = await axios.get(`${API_URL}/marketplace-walkin-drives`, { params: customParams });
      if (response.data.success && Array.isArray(response.data.data)) {
        setWalkinDrives(response.data.data);
      } else {
        setWalkinDrives([]);
      }
    } catch (err) {
      console.error("Failed to fetch active walk-in drives", err);
      setWalkinDrives([]);
    }
  };

  const [pendingScreenings, setPendingScreenings] = React.useState([]);

  const checkCandidateScreenings = async () => {
    const cand = candidateAuthService.getCurrentCandidate() || JSON.parse(localStorage.getItem('user') || '{}');
    if (candidateAuthService.isAuthenticated() || cand?.email) {
      try {
        const res = await candidateAuthService.getMyScreenings();
        if (res.success && Array.isArray(res.data)) {
          const aiScreenings = res.data.filter(s => {
            const isCompleted = s.ai_screening_status === 'COMPLETED' || 
                                s.ai_screening_status === 'PASSED' || 
                                s.ai_screening_status === 'FAILED';
            return !isCompleted;
          });
          setPendingScreenings(aiScreenings);
        }
      } catch (e) {
        console.log("Candidate screenings check error:", e);
      }
    } else {
      setPendingScreenings([]);
    }
  };

  React.useEffect(() => {
    fetchJobs();
    fetchWalkinDrives();
    checkCandidateScreenings();

    const sub = candidateAuthService.onAuthStateChange(() => {
      fetchJobs();
      checkCandidateScreenings();
    });
    window.addEventListener('authChange', fetchJobs);
    return () => {
      sub.unsubscribe();
      window.removeEventListener('authChange', fetchJobs);
    };
  }, []);

  const handleSearch = (searchData) => {
    const newSearchParams = {
      keyword: searchData.keyword ?? '',
      location: searchData.location ?? ''
    };
    setSearchParams(newSearchParams);
    setCurrentPage(1);

    const apiParams = {
      search: newSearchParams.keyword,
      location: newSearchParams.location,
      type: filters.jobType[0] || ''
    };
    fetchJobs(apiParams);
    fetchWalkinDrives({ search: newSearchParams.keyword, location: newSearchParams.location });
  };

  const handleFilterChange = (updatedFilters) => {
    setFilters(updatedFilters);
    setCurrentPage(1);

    const effectiveKeyword = updatedFilters.keyword !== undefined && updatedFilters.keyword !== '' 
      ? updatedFilters.keyword 
      : searchParams.keyword;
    const effectiveLocation = updatedFilters.location !== undefined && updatedFilters.location !== '' 
      ? updatedFilters.location 
      : searchParams.location;

    const apiParams = {
      search: effectiveKeyword,
      location: effectiveLocation,
      type: updatedFilters.jobType[0] || ''
    };
    fetchJobs(apiParams);
    fetchWalkinDrives({ search: effectiveKeyword, location: effectiveLocation });
  };

  const handleFilterReset = () => {
    setSearchParams({ keyword: '', location: '' });
    setFilters({
      keyword: '',
      location: '',
      jobType: [],
      experience: '',
      salary: { min: '', max: '' },
      companySize: [],
      industry: '',
      postedWithin: ''
    });
    setCurrentPage(1);
    fetchJobs();
    fetchWalkinDrives();
  };

  const handleSaveJob = (jobId) => {
    setSavedJobs((prev) =>
      prev?.includes(jobId) ?
        prev?.filter((id) => id !== jobId) :
        [...prev, jobId]
    );
  };

  const handleApplyJob = (job) => {
    setSelectedJob(job);
    setPendingJob(job);
    if (candidateAuthService.isAuthenticated()) {
      setShowApplyModal(true);
    } else {
      setShowCandidateAuthModal(true);
    }
  };

  const handleCandidateAuthSuccess = (candidateUser) => {
    setShowCandidateAuthModal(false);
    const targetJob = pendingJob || selectedJob;
    if (targetJob) {
      setSelectedJob(targetJob);
      setShowApplyModal(true);
    }
  };

  const handleViewDetails = (job) => {
    setSelectedJob(job);
  };

  const handleApplySuccess = (applicationId) => {
    setShowApplyModal(false);
    setSelectedJob(null);
    setPendingJob(null);
    if (applicationId) {
      navigate(`/ai-screening/${applicationId}`);
      return;
    }
    setApplySuccess(true);
    fetchJobs(); // Refresh jobs to update applicant count
    setTimeout(() => setApplySuccess(false), 5000); // Hide success message after 5s
  };

  const filteredJobs = React.useMemo(() => {
    let result = jobs.filter(job => {
      // Keyword matching (title, company, description, or skills)
      const kw = (searchParams.keyword || filters.keyword || '').toLowerCase().trim();
      if (kw) {
        const titleMatch = job.title?.toLowerCase().includes(kw);
        const companyMatch = job.company?.toLowerCase().includes(kw);
        const descMatch = job.description?.toLowerCase().includes(kw);
        
        let skillsMatch = false;
        if (Array.isArray(job.skills)) {
          skillsMatch = job.skills.some(s => typeof s === 'string' && s.toLowerCase().includes(kw));
        } else if (typeof job.skills === 'string') {
          skillsMatch = job.skills.toLowerCase().includes(kw);
        }

        if (!titleMatch && !companyMatch && !descMatch && !skillsMatch) {
          return false;
        }
      }

      // Location matching
      const loc = (searchParams.location || filters.location || '').toLowerCase().trim();
      if (loc) {
        const locMatch = job.location?.toLowerCase().includes(loc);
        if (!locMatch) return false;
      }

      // Job Type matching
      if (filters.jobType && filters.jobType.length > 0) {
        const typeMatch = filters.jobType.some(t => {
          const formattedT = t.toLowerCase().replace(/[-_]/g, '');
          const formattedJobType = (job.type || '').toLowerCase().replace(/[-_]/g, '');
          return formattedJobType.includes(formattedT) || formattedT.includes(formattedJobType);
        });
        if (!typeMatch) return false;
      }

      // Experience Level matching
      if (filters.experience) {
        const expFilter = filters.experience.toLowerCase();
        const jobExp = (job.experience || '').toLowerCase();
        if (expFilter === 'entry' && !(jobExp.includes('0-2') || jobExp.includes('entry') || jobExp.includes('fresh') || jobExp.includes('1') || jobExp.includes('2'))) return false;
        if (expFilter === 'mid' && !(jobExp.includes('3-5') || jobExp.includes('mid') || jobExp.includes('3') || jobExp.includes('4') || jobExp.includes('5'))) return false;
        if (expFilter === 'senior' && !(jobExp.includes('6-10') || jobExp.includes('senior') || jobExp.includes('6') || jobExp.includes('7') || jobExp.includes('8'))) return false;
        if (expFilter === 'lead' && !(jobExp.includes('10+') || jobExp.includes('lead') || jobExp.includes('manager'))) return false;
      }

      // Salary Range matching
      if (filters.salary?.min) {
        const minVal = parseInt(filters.salary.min);
        if (!isNaN(minVal) && job.salaryMax && job.salaryMax < minVal) return false;
      }
      if (filters.salary?.max) {
        const maxVal = parseInt(filters.salary.max);
        if (!isNaN(maxVal) && job.salaryMin && job.salaryMin > maxVal) return false;
      }

      return true;
    });

    // Sorting logic
    if (sortBy === 'recent') {
      result.sort((a, b) => new Date(b.postedDate || 0) - new Date(a.postedDate || 0));
    } else if (sortBy === 'salary-high') {
      result.sort((a, b) => (b.salaryMax || 0) - (a.salaryMax || 0));
    } else if (sortBy === 'salary-low') {
      result.sort((a, b) => (a.salaryMin || 0) - (b.salaryMin || 0));
    }

    return result;
  }, [jobs, searchParams, filters, sortBy]);

  const totalPages = Math.ceil(filteredJobs?.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const displayedJobs = filteredJobs?.slice(startIndex, startIndex + jobsPerPage);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  return (
    <>
      <Helmet>
        <title>Job Marketplace - InChi Solutions Hub</title>
        <meta name="description" content="Find your dream job with InChi Solutions Pvt Ltd. Browse thousands of job opportunities across various industries and locations." />
      </Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <Header />

        <main className="flex-1 pt-20">
          {/* COMPACT NAUKRI-STYLE HERO SECTION */}
          <div className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-4 md:py-6 border-b border-border/40">
            <div className="max-w-7xl mx-auto px-4 md:px-6 space-y-3">
              <div className="text-center">
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mb-1 tracking-tight">
                  Find Your Dream Job
                </h1>
                <p className="text-xs md:text-sm text-muted-foreground max-w-xl mx-auto">
                  Discover opportunities from top companies. Start your career journey with InChi Solutions Pvt Ltd.
                </p>
              </div>

              <SearchBar
                onSearch={handleSearch}
                initialKeyword={searchParams.keyword}
                initialLocation={searchParams.location}
              />

              <QuickStats stats={stats} />
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
              <aside className="lg:col-span-1">
                <div className="sticky top-24">
                  <SearchFilters
                    onFilterChange={handleFilterChange}
                    onReset={handleFilterReset} />

                </div>
              </aside>

              <div className="lg:col-span-3 space-y-6">

                {/* CANDIDATE PENDING AI SCREENING BANNER */}
                {pendingScreenings && pendingScreenings.length > 0 && (
                  <div className="space-y-3 animate-fade-in">
                    {pendingScreenings.map((sc) => (
                      <PendingAiScreeningCard key={sc.id} sc={sc} navigate={navigate} />
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-card rounded-lg p-4">
                  <div>
                    <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">
                      {filteredJobs?.length?.toLocaleString()} Jobs Found
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Showing {filteredJobs?.length > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + jobsPerPage, filteredJobs?.length || 0)} of {filteredJobs?.length || 0}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e?.target?.value)}
                      className="flex-1 sm:flex-none px-4 py-2 rounded-md border border-border bg-background text-foreground text-sm">

                      <option value="relevance">Most Relevant</option>
                      <option value="recent">Most Recent</option>
                      <option value="salary-high">Salary: High to Low</option>
                      <option value="salary-low">Salary: Low to High</option>
                    </select>

                    <div className="flex gap-2">
                      <button
                        onClick={() => setViewMode('list')}
                        className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`
                        }
                        aria-label="List view">

                        <Icon name="List" size={18} />
                      </button>
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`
                        }
                        aria-label="Grid view">

                        <Icon name="Grid" size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* ACTIVE WALK-IN HIRING DRIVES */}
                {walkinDrives && walkinDrives.length > 0 && (
                  <div className="space-y-3 mb-6 animate-fade-in">
                    <div className="flex items-center justify-between pb-1">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-primary/15 text-primary flex items-center justify-center font-bold shadow-sm">
                          <Icon name="Zap" size={18} />
                        </div>
                        <div>
                          <h2 className="text-lg md:text-xl font-extrabold text-foreground flex items-center gap-2">
                            <span>Active Walk-in Hiring Drives</span>
                            <span className="px-2.5 py-0.5 rounded-full bg-primary/15 text-primary text-xs font-extrabold">
                              {walkinDrives.length} Live
                            </span>
                          </h2>
                          <p className="text-xs text-muted-foreground">
                            Fast on-site walk-in interviews with live queue assignment • No login required
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {walkinDrives.map((drive) => (
                        <WalkinDriveCard key={drive.id} drive={drive} />
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-12 bg-card rounded-lg border border-dashed border-border">
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-muted-foreground">Finding the best opportunities for you...</p>
                    </div>
                  ) : error ? (
                    <div className="p-8 text-center bg-card rounded-lg border border-error/20 text-error">
                      <Icon name="AlertCircle" size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="font-semibold">{error}</p>
                      <Button variant="outline" size="sm" onClick={() => fetchJobs()} className="mt-4">
                        Try Again
                      </Button>
                    </div>
                  ) : displayedJobs?.length > 0 ? (
                    <>
                      {applySuccess && (
                        <div className="bg-success/10 border border-success/20 p-4 rounded-lg flex items-center gap-3 animate-fade-in mb-4">
                          <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center text-success flex-shrink-0">
                            <Icon name="CheckCircle2" size={20} />
                          </div>
                          <div>
                            <p className="font-semibold text-foreground">Application Submitted!</p>
                            <p className="text-sm text-muted-foreground">Your application has been successfully sent to the employer.</p>
                          </div>
                        </div>
                      )}
                      {displayedJobs.map((job) => (
                        <JobCard
                          key={job.id}
                          job={job}
                          onSave={handleSaveJob}
                          onApply={handleApplyJob}
                          onViewDetails={handleViewDetails}
                          isSaved={savedJobs.includes(job.id)}
                        />
                      ))}
                    </>
                  ) : (
                    <div className="p-12 text-center bg-card rounded-lg border border-dashed border-border">
                      <Icon name="SearchX" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">No jobs found</h3>
                      <p className="text-muted-foreground">Try adjusting your search or filters to find what you're looking for.</p>
                      <Button variant="outline" size="sm" onClick={handleFilterReset} className="mt-4">
                        Clear All Filters
                      </Button>
                    </div>
                  )}
                </div>

                {totalPages > 1 &&
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-card rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        iconName="ChevronLeft"
                        iconPosition="left">

                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        iconName="ChevronRight"
                        iconPosition="right">

                        Next
                      </Button>
                    </div>
                  </div>
                }

                <JobAlerts />
              </div>
            </div>
          </div>
        </main>

        <Footer />

        {!showApplyModal && selectedJob && (
          <JobDetailsModal
            job={selectedJob}
            onClose={() => setSelectedJob(null)}
            onApply={() => handleApplyJob(selectedJob)}
          />
        )}

        {showCandidateAuthModal && (
          <CandidateAuthModal
            isOpen={showCandidateAuthModal}
            job={pendingJob || selectedJob}
            initialMode="login"
            onClose={() => {
              setShowCandidateAuthModal(false);
              setPendingJob(null);
            }}
            onSuccess={handleCandidateAuthSuccess}
          />
        )}

        {showApplyModal && selectedJob && (
          <ApplyModal
            job={selectedJob}
            onClose={() => setShowApplyModal(false)}
            onSuccess={handleApplySuccess}
          />
        )}
      </div>
    </>
  );
};

export default JobMarketplace;